import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ref, get } from 'firebase/database';
import { db } from '../../config/firebase';
import { Spinner } from '../../components/ui/Spinner';

export function normalizeVerificationStudents(students) {
  if (!students) return [];
  if (Array.isArray(students)) return students;
  return Object.values(students);
}

const VerificationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const batchParam = searchParams.get('batch');

  useEffect(() => {
    const fetchBatches = async () => {
      try {
        const verificationRef = ref(db, 'verification');
        const snapshot = await get(verificationRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          const list = Object.entries(data).map(([key, batch]) => ({
            ...batch,
            _key: key,
            students: normalizeVerificationStudents(batch.students),
          }));
          setBatches(list);
        } else {
          setBatches([]);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching batches:', err);
        setError('Failed to load verification data');
        setLoading(false);
      }
    };

    fetchBatches();
  }, []);

  useEffect(() => {
    if (!batchParam) {
      setSelectedBatch(null);
      return;
    }
    if (loading) return;
    const found = batches.find((b) => b._key === batchParam);
    setSelectedBatch(found || null);
  }, [batchParam, batches, loading]);

  const openBatch = useCallback(
    (batch) => {
      setSelectedBatch(batch);
      setSearchParams({ batch: batch._key });
    },
    [setSearchParams],
  );

  const closeBatch = useCallback(() => {
    setSelectedBatch(null);
    setSearchParams({});
  }, [setSearchParams]);

  const BatchCard = ({ batch }) => (
    <div
      onClick={() => openBatch(batch)}
      className="bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
    >
      <h3 className="text-xl font-semibold text-gray-800 mb-2">
        {batch.programName}
      </h3>
      <div className="space-y-2 text-gray-600">
        <p>Batch: {batch.batch}</p>
        <p>Year: {batch.year}</p>
        <p>Level: {batch.programLevel}</p>
        <p className="text-sm text-primary-600">
          {batch.students.length} Students Completed
        </p>
      </div>
    </div>
  );

  const studentsForTable = useMemo(() => {
    if (!selectedBatch) return [];
    return normalizeVerificationStudents(selectedBatch.students);
  }, [selectedBatch]);

  const BatchDetails = ({ batch }) => (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {batch.programName}
          </h2>
          <p className="text-gray-600">
            Batch {batch.batch} • {batch.year}
          </p>
        </div>
        <button
          type="button"
          onClick={closeBatch}
          className="text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Program Details</h3>
        <div className="grid grid-cols-2 gap-4 text-gray-600">
          <p>Level: {batch.programLevel}</p>
          <p>Certification: {batch.metadata?.certificationBody ?? '—'}</p>
          <p>Completed: {batch.metadata?.completionDate ?? '—'}</p>
          <p>
            Total Students:{' '}
            {batch.metadata?.totalStudents ?? studentsForTable.length}
          </p>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-4">Graduated Students</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Special Achievements
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {studentsForTable.map((student, idx) => (
                <tr key={student.id || `${student.name}-${idx}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {student.id || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {student.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {student.specialAchievements || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-600 p-4">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-center mb-8">
        Course Completion Verification
      </h1>

      {selectedBatch ? (
        <BatchDetails batch={selectedBatch} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {batches.map((batch) => (
            <BatchCard key={batch._key} batch={batch} />
          ))}
        </div>
      )}
    </div>
  );
};

export default VerificationPage;
